import { Link } from "react-router-dom";
import { Linkedin, Facebook, Mail } from "lucide-react";
import carbonwizeLogoLight from "@/assets/carbonwize-logo-white.png";
import tuvLogo from "@/assets/tuv-iso-logo.png";
import cfoLogo from "@/assets/cfo-platform-logo.png";
import setLogo from "@/assets/set-logo.png";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

const Footer = () => {
  const { t } = useLanguage();
  const { getLocalizedPath } = useLocalizedPath();

  const footerLinks = {
    company: [
      { name: t("footer.aboutUs"), href: "/about-us", isInternal: true },
      { name: t("footer.contact"), href: "/contact-us", isInternal: true },
      { name: t("footer.becomePartner"), href: "/become-partner", isInternal: true },
    ],
    resources: [
      { name: t("footer.blogs"), href: "/blog", isInternal: true },
      { name: t("footer.testimonials"), href: "/#testimonials", isInternal: false },
      { name: t("footer.integrations"), href: "/other-services", isInternal: true },
      { name: t("footer.support"), href: "/other-services", isInternal: true },
    ],
    siteInfo: [
      { name: t("footer.termsOfService"), href: "/terms-of-service", isInternal: true },
      { name: t("footer.privacyPolicy"), href: "/privacy-policy", isInternal: true },
    ],
  };

  return (
    <footer className="bg-dark text-dark-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 lg:py-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to={getLocalizedPath("/")} className="flex items-center mb-4">
              <img src={carbonwizeLogoLight} alt="Carbonwize - Enterprise carbon accounting software Thailand" style={{ width: '100px' }} />
            </Link>
            <p className="text-dark-foreground/60 max-w-xs mb-6">
              {t("footer.description")}
            </p>
            <div className="flex gap-4">
              <a href="https://www.linkedin.com/company/carbonwizeio" target="_blank" rel="noopener noreferrer" aria-label="Follow Carbonwize on LinkedIn" className="w-10 h-10 rounded-full bg-dark-foreground/10 flex items-center justify-center hover:bg-accent transition-colors duration-300">
                <Linkedin className="w-5 h-5" aria-hidden="true" />
              </a>
              <a href="https://www.facebook.com/carbonwize/" target="_blank" rel="noopener noreferrer" aria-label="Follow Carbonwize on Facebook" className="w-10 h-10 rounded-full bg-dark-foreground/10 flex items-center justify-center hover:bg-accent transition-colors duration-300">
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
              <a href="mailto:info@carbonwize.io" aria-label="Email Carbonwize at info@carbonwize.io" className="w-10 h-10 rounded-full bg-dark-foreground/10 flex items-center justify-center hover:bg-accent transition-colors duration-300">
                <Mail className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-semibold mb-4 text-base">{t("footer.company")}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map(link => (
                <li key={link.name}>
                  {link.isInternal ? (
                    <Link to={getLocalizedPath(link.href)} className="text-dark-foreground/60 hover:text-accent transition-colors duration-200">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-dark-foreground/60 hover:text-accent transition-colors duration-200">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-base">{t("footer.resources")}</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map(link => (
                <li key={link.name}>
                  {link.isInternal ? (
                    <Link to={getLocalizedPath(link.href)} className="text-dark-foreground/60 hover:text-accent transition-colors duration-200">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-dark-foreground/60 hover:text-accent transition-colors duration-200">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-base">{t("footer.siteInfo")}</h3>
            <ul className="space-y-3">
              {footerLinks.siteInfo.map(link => (
                <li key={link.name}>
                  {link.isInternal ? (
                    <Link to={getLocalizedPath(link.href)} className="text-dark-foreground/60 hover:text-accent transition-colors duration-200">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-dark-foreground/60 hover:text-accent transition-colors duration-200">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Certifications Section */}
        <div className="py-8 border-t border-dark-foreground/10">
          <div className="flex flex-col items-start justify-center gap-6 md:flex-row md:items-center md:gap-8">
            {/* TUV Certification */}
            <div className="flex items-center gap-4 w-full md:w-80">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                <img src={tuvLogo} alt="TÜV SÜD ISO/IEC 29110 certification - Carbonwize certified software engineering standard" className="max-h-16 max-w-16 object-contain" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-dark-foreground">{t("footer.certifiedSystem")}</p>
                <p className="text-sm font-medium text-dark-foreground">{t("footer.engineeringStandard")}</p>
                <p className="text-xs text-dark-foreground/60">ISO/IEC 29110</p>
              </div>
            </div>

            {/* SET Partnership */}
            <Link to={getLocalizedPath("/blog/carbonwize_setcarbon_platform")} className="flex items-center gap-4 w-full md:w-80 hover:opacity-80 transition-opacity">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                <img src={setLogo} alt="Stock Exchange of Thailand official partner - SET Carbon platform collaboration" className="max-h-16 max-w-16 object-contain" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-dark-foreground">{t("footer.setPartner")}</p>
                <p className="text-xs text-dark-foreground/60">{t("footer.setCarbon")}</p>
              </div>
            </Link>

            {/* CFO Platform Certification */}
            <Link to={getLocalizedPath("/blog/tgo-cfo-platform")} className="flex items-center gap-4 w-full md:w-80 hover:opacity-80 transition-opacity">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                <img src={cfoLogo} alt="TGO CFO Platform certification - Thailand carbon footprint organization approved" className="max-h-16 max-w-16 object-contain" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-dark-foreground">{t("footer.certifiedCFO")}</p>
                <p className="text-xs text-dark-foreground/60">{t("footer.enterpriseReady")}</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-dark-foreground/10 flex justify-center items-center">
          <p className="text-sm text-dark-foreground/60">
            © {new Date().getFullYear()} {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
